import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CommunityInlineCodeChip } from './community-discount-chip';

// Nhận diện URL trong text thuần (http/https/www.) — capture group giữ lại
// khớp khi split để còn dựng lại đúng thứ tự đoạn text/đoạn link.
const URL_SPLIT_REGEX = /((?:https?:\/\/|www\.)[^\s]+)/gi;
const URL_TEST_REGEX = /^(?:https?:\/\/|www\.)/i;
// Bỏ dấu câu cuối URL (., , ) ] ! ?) ra khỏi link để không kéo theo câu văn.
const TRAILING_PUNCT_REGEX = /[.,!?)\]]+$/;

// #3: link chỉ tô màu chữ xanh, KHÔNG tô nền vàng (yêu cầu UX mới).
const DEFAULT_LINK_CLASS = 'font-semibold text-blue-700 break-all';

// #4: markdown-lite — chỉ nhận **bold** và *italic*. Yêu cầu ký tự đầu/cuối
// bên trong cặp dấu * không phải khoảng trắng để giảm ăn nhầm case như dấu
// "*" lẻ hoặc phép nhân "2*3" (không đủ cặp * nên không khớp). Bold parse
// TRƯỚC italic (tách theo cặp ** trước) để 1 đoạn **đậm** không bị hiểu lầm
// thành 2 lượt *nghiêng* lồng nhau.
// Cho phép xuống dòng bên trong cặp dấu ([^*] thay vì [^*\n]) — bôi đen nhiều
// dòng rồi bấm B vẫn in đậm được, và khớp với renderer app
// (mobile/src/modules/community-channel/utils/community-format.ts).
//
// ĐẬM nới lỏng: chỉ cần có ÍT NHẤT 1 ký tự không phải khoảng trắng bên trong,
// cho phép dính dấu cách sát cặp dấu (`** MINIGAME 07**` — bài cũ gõ tay hay
// bôi đen dư dấu cách vẫn in đậm). Cặp `**` lạc rất hiếm nên nới an toàn.
// NGHIÊNG giữ chặt (ký tự sát dấu * phải là non-space) để không biến
// "3 * 4 * 5" thành chữ nghiêng.
const BOLD_SPLIT_REGEX = /(\*\*[^*]*\S[^*]*\*\*)/g;
const BOLD_FULL_REGEX = /^\*\*([^*]*\S[^*]*)\*\*$/;
const ITALIC_SPLIT_REGEX = /(\*\S(?:[^*]*\S)?\*)/g;
const ITALIC_FULL_REGEX = /^\*(\S(?:[^*]*\S)?)\*$/;
// ~~gạch ngang~~ — không chứa dấu * nên tách trước bold/italic, không đụng nhau.
const STRIKE_SPLIT_REGEX = /(~~[^~]*\S[^~]*~~)/g;
const STRIKE_FULL_REGEX = /^~~([^~]*\S[^~]*)~~$/;
// Link có chữ hiển thị: [Đăng ký ngay](https://...) — phải tách TRƯỚC link tự
// nhận diện, nếu không URL trong ngoặc bị bắt thành link trần và vỡ cú pháp.
const LABELED_LINK_SPLIT_REGEX = /(\[[^\]\n]+\]\((?:https?:\/\/|www\.)[^\s)]+\))/g;
const LABELED_LINK_FULL_REGEX = /^\[([^\]\n]+)\]\(((?:https?:\/\/|www\.)[^\s)]+)\)$/;

// Mã giảm giá INLINE: gõ #SHPCCCC trong nội dung -> chip vàng bấm-copy.
// Rule: sau # là 4-20 ký tự IN HOA/số, KHÔNG dính thêm chữ/số phía sau
// (lookahead chặn khớp nửa từ). Hashtag thường chữ thường (#sale) giữ nguyên.
// KHÔNG dùng lookbehind — Safari <16.4 crash parse regex là vỡ cả trang.
const INLINE_CODE_SPLIT_REGEX = /(#[A-Z0-9]{4,20}(?![A-Za-z0-9]))/g;
const INLINE_CODE_FULL_REGEX = /^#([A-Z0-9]{4,20})$/;

/**
 * LÁ của cây parse: chip mã `#MÃ` + link trần. Style (đậm/nghiêng/gạch) đã được
 * bóc ở các tầng trên nên ở đây chỉ còn chữ và link.
 */
function renderLeaf(text, keyPrefix, linkClassName) {
    if (!text) return null;
    return text.split(INLINE_CODE_SPLIT_REGEX).map((codePart, cIdx) => {
        if (!codePart) return null;
        const codeMatch = codePart.match(INLINE_CODE_FULL_REGEX);
        if (codeMatch) {
            return <CommunityInlineCodeChip key={`${keyPrefix}-c${cIdx}`} code={codeMatch[1]} />;
        }
        return (
            <React.Fragment key={`${keyPrefix}-c${cIdx}`}>
                {renderBareLinked(codePart, linkClassName)}
            </React.Fragment>
        );
    });
}

/** Parse `~~gạch ngang~~` rồi tới bold/italic. */
function renderStrike(text, keyPrefix, linkClassName) {
    if (!text) return null;
    return text.split(STRIKE_SPLIT_REGEX).map((part, sIdx) => {
        if (!part) return null;
        const key = `${keyPrefix}-s${sIdx}`;
        const strikeMatch = part.match(STRIKE_FULL_REGEX);
        if (strikeMatch) {
            return (
                <span key={key} className="line-through">
                    {renderBoldItalic(strikeMatch[1], key, linkClassName)}
                </span>
            );
        }
        return (
            <React.Fragment key={key}>
                {renderBoldItalic(part, key, linkClassName)}
            </React.Fragment>
        );
    });
}

/**
 * Parse `**bold**`/`*italic*`, rồi mới xuống lá (chip mã + link trần).
 *
 * Thứ tự bắt buộc: bóc style TRƯỚC, tìm link SAU. Làm ngược lại thì
 * `**https://a.vn**` bị cắt thành `**` + link + `**`, hai dấu ** rơi vào hai đoạn
 * khác nhau nên không ghép được thành cặp -> dấu ** hiện nguyên ra màn hình.
 */
function renderBoldItalic(text, keyPrefix, linkClassName) {
    if (!text) return null;
    return text.split(BOLD_SPLIT_REGEX).map((boldPart, bIdx) => {
        if (!boldPart) return null;
        const boldMatch = boldPart.match(BOLD_FULL_REGEX);
        if (boldMatch) {
            return (
                <strong key={`${keyPrefix}-b${bIdx}`}>
                    {renderLeaf(boldMatch[1], `${keyPrefix}-b${bIdx}`, linkClassName)}
                </strong>
            );
        }
        return boldPart.split(ITALIC_SPLIT_REGEX).map((italicPart, iIdx) => {
            if (!italicPart) return null;
            const key = `${keyPrefix}-b${bIdx}-i${iIdx}`;
            const italicMatch = italicPart.match(ITALIC_FULL_REGEX);
            if (italicMatch) {
                return <em key={key}>{renderLeaf(italicMatch[1], key, linkClassName)}</em>;
            }
            return (
                <React.Fragment key={key}>
                    {renderLeaf(italicPart, key, linkClassName)}
                </React.Fragment>
            );
        });
    });
}

/**
 * Tách text thuần thành list node React, link tự nhận diện được bọc <a>.
 * `linkClassName` cho phép caller đổi style link (vd: nền gradient FB-style
 * cần link trắng gạch chân thay vì nền vàng mặc định). Đoạn text ngoài link
 * được parse thêm bold/italic markdown-lite (#4).
 */
export function renderLinkified(text, linkClassName = DEFAULT_LINK_CLASS) {
    if (!text) return null;
    // Link có chữ hiển thị tách trước, phần còn lại mới dò link trần
    return String(text)
        .split(LABELED_LINK_SPLIT_REGEX)
        .map((chunk, cIdx) => {
            if (!chunk) return null;
            const labeled = chunk.match(LABELED_LINK_FULL_REGEX);
            if (!labeled) {
                return (
                    <React.Fragment key={`l${cIdx}`}>
                        {renderStrike(chunk, `l${cIdx}`, linkClassName)}
                    </React.Fragment>
                );
            }
            const url = labeled[2];
            return (
                <a
                    key={`l${cIdx}`}
                    href={url.startsWith('www.') ? `https://${url}` : url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className={linkClassName}
                >
                    {labeled[1]}
                </a>
            );
        });
}

/** Dò link trần (http/www) trong đoạn đã loại link có chữ hiển thị. */
function renderBareLinked(text, linkClassName) {
    return String(text)
        .split(URL_SPLIT_REGEX)
        .map((part, idx) => {
            if (!part) return null;
            if (!URL_TEST_REGEX.test(part)) {
                return <React.Fragment key={idx}>{part}</React.Fragment>;
            }
            const trailingMatch = part.match(TRAILING_PUNCT_REGEX);
            const trailing = trailingMatch ? trailingMatch[0] : '';
            const cleanUrl = trailing ? part.slice(0, -trailing.length) : part;
            const href = cleanUrl.startsWith('www.') ? `https://${cleanUrl}` : cleanUrl;
            return (
                <React.Fragment key={idx}>
                    <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className={linkClassName}
                    >
                        {cleanUrl}
                    </a>
                    {trailing}
                </React.Fragment>
            );
        });
}

/**
 * Nội dung bài đăng: text thuần, link tự nhận diện (đậm + nền vàng nhạt,
 * mở tab mới, copy được vì vẫn là text thường). Ở chế độ feed (expandable),
 * clamp 5 dòng + nút "Xem thêm"/"Thu gọn" chỉ hiện khi nội dung thực sự bị cắt.
 */
function LinkifiedText({ text, expandable = true }) {
    const [expanded, setExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const ref = useRef(null);

    useLayoutEffect(() => {
        if (!expandable || !ref.current) {
            setIsTruncated(false);
            return;
        }
        setIsTruncated(ref.current.scrollHeight > ref.current.clientHeight + 1);
    }, [text, expandable]);

    // Reset trạng thái mở rộng khi nội dung đổi (đổi bài đang xem trong list)
    useEffect(() => {
        setExpanded(false);
    }, [text]);

    const clamp = expandable && !expanded;

    return (
        <div>
            <p
                ref={ref}
                className={`whitespace-pre-wrap break-words text-[15px] text-content leading-relaxed ${
                    clamp ? 'line-clamp-5' : ''
                }`}
            >
                {renderLinkified(text)}
            </p>
            {expandable && isTruncated && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setExpanded((v) => !v);
                    }}
                    className="text-xs font-semibold text-primary-600 mt-1 hover:underline"
                >
                    {expanded ? 'Thu gọn' : 'Xem thêm'}
                </button>
            )}
        </div>
    );
}

export default LinkifiedText;
